import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import HeroSection from '../components/HeroSection';
import WhyTrustUs from '../components/WhyTrustUs';
import Features from '../components/Features';
import AppShowcase from '../components/AppShowcase';
import TokenTicker from '../components/TokenTicker';
import LiveResultsBanner from '../components/LiveResultsBanner';
import ClosedSignalsTicker from '../components/ClosedSignalsTicker';
import ActiveSignalsTicker from '../components/ActiveSignalsTicker';

const HomePageContainer = styled.div``;

const HomePage = () => {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    "name": "Trend Genie - Free AI Crypto Signals App",
    "operatingSystem": "Android",
    "applicationCategory": "FinanceApplication",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "ratingCount": "104"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Trend Genie is an AI crypto signals app for Android that scans the crypto market in real time, generates complete trade setups, provides AI chart analysis, DeFi token scanning, crypto news, and community discussion tools.",
    "image": ["https://www.trendgenie.io/social-share-image.png"],
    "url": "https://www.trendgenie.io",
    "downloadUrl": "https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie",
    "author": {
      "@type": "Organization",
      "name": "Trend Genie",
      "url": "https://www.trendgenie.io"
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Trend Genie",
    "url": "https://www.trendgenie.io",
    "logo": "https://www.trendgenie.io/logo.png",
    "sameAs": [
      "https://x.com/trendgenie_ai"
    ],
    "description": "Trend Genie is a crypto trading ecosystem that combines an AI crypto signals app, DeFi token scanner, crypto community platform, and token-powered product suite."
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is Trend Genie a free crypto signals app?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Trend Genie is available on Android and offers a seven-day full-access trial so users can test real-time AI crypto signals, chart analysis, and trading tools."
        }
      },
      {
        "@type": "Question",
        "name": "What does the Trend Genie app do?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Trend Genie analyzes the crypto market in real time, detects trade setups, and shows entry price, take profit targets, and stop loss levels. Users can also upload chart screenshots, run AI coin analysis, follow crypto news, and chat with other traders."
        }
      },
      {
        "@type": "Question",
        "name": "Can beginners use Trend Genie?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. The app is designed so even new traders can understand setups because every signal includes structured entry, take profit, and stop loss information in a simple format."
        }
      },
      {
        "@type": "Question",
        "name": "What is included in the premium plan?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Premium access is available for 10 USD per month after the free trial and unlocks the full set of Trend Genie app features, while free users still keep access to selected community and insight features."
        }
      }
    ]
  };

  return (
    <HomePageContainer>
      <Helmet>
        <title>Trend Genie | AI Crypto Signals App, RugShield Scanner and Crypto Community</title>
        <meta
          name="description"
          content="Trend Genie is an AI crypto signals app for Android that scans the market in real time, delivers entry, take-profit and stop-loss levels, offers AI chart analysis, DeFi token scanning, crypto news, and a built-in trader community."
        />
        <meta
          name="keywords"
          content="AI crypto signals app, crypto signals app android, real time crypto signals, AI chart analysis crypto, crypto trading signals app, rug pull checker, DeFi token scanner, crypto community app, crypto market analysis app, Trend Genie"
        />
        <link rel="canonical" href="https://www.trendgenie.io/" />
        <meta property="og:title" content="Trend Genie | AI Crypto Signals App for Android" />
        <meta
          property="og:description"
          content="Get real-time crypto trade setups, AI chart analysis, DeFi token scanning, live crypto news, and community discussion tools inside the Trend Genie ecosystem."
        />
        <meta property="og:image" content="https://www.trendgenie.io/social-share-image.png" />
        <meta property="og:url" content="https://www.trendgenie.io/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Trend Genie" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@trendgenie_ai" />
        <meta name="twitter:title" content="Trend Genie | AI Crypto Signals App" />
        <meta
          name="twitter:description"
          content="AI-powered crypto signals for Android with complete trade setups, chart analysis, DeFi scanning, crypto news, and trader community features."
        />
        <meta name="twitter:image" content="https://www.trendgenie.io/social-share-image.png" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="Trend Genie" />
        <meta name="theme-color" content="#ffffff" />
        <meta name="robots" content="index, follow" />
        <meta
          name="googlebot"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <HeroSection />

      <ClosedSignalsTicker />
      <ActiveSignalsTicker />
      <LiveResultsBanner />
      <TokenTicker />
      <Features />
      <AppShowcase />
      <WhyTrustUs />
    </HomePageContainer>
  );
};

export default HomePage;
